import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { ClockCounterClockwise, FolderOpen, ArrowLeft } from "@phosphor-icons/react";

const TONE = { "Belum ada": "bg-slate-100 text-slate-500", Draft: "bg-amber-100 text-amber-700", Disahkan: "bg-emerald-100 text-emerald-700", "Ada Adendum": "bg-indigo-100 text-indigo-700" };

export default function PatientHistory() {
  const { pid } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    document.title = "Riwayat Pasien — HalloGBS";
    api.get(`/rme/patients/${pid}/history`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [pid]);

  if (!data) return <div className="p-10 text-center text-sm text-slate-400" data-testid="patient-history-loading">Memuat…</div>;
  const p = data.patient;

  return (
    <div className="space-y-5" data-testid="patient-history">
      <button onClick={() => nav(-1)} data-testid="ph-back" className="flex items-center gap-1 text-sm font-bold text-[#7C4DE0] hover:underline"><ArrowLeft size={16} weight="bold" /> Kembali</button>

      <div className="rounded-2xl border border-pink-100 bg-white p-4">
        <h1 className="flex items-center gap-2 font-heading text-xl font-extrabold text-[#4C1D95]"><ClockCounterClockwise size={22} weight="duotone" className="text-[#DB2777]" /> Riwayat Pasien</h1>
        <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <span className="font-bold text-slate-800" data-testid="ph-nama">{p.nama_lengkap}</span>
          <span className="font-mono text-slate-500">No. RM {p.no_rm}</span>
          <span className="font-mono text-slate-500">NIK {p.nik_masked}</span>
          <span className="text-slate-500">{p.jenis_kelamin || "-"}</span>
        </div>
        {!data.can_view_clinical && (
          <p data-testid="ph-admin-note" className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700">Ringkasan administratif. Isi SOAP &amp; catatan klinis hanya untuk peran klinis yang berwenang.</p>
        )}
      </div>

      {data.timeline.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-pink-200 bg-white/50 p-10 text-center text-sm text-slate-400" data-testid="ph-empty">Belum ada riwayat kunjungan klinis.</div>
      ) : (
        <ol className="relative space-y-4 border-l-2 border-pink-100 pl-5" data-testid="ph-timeline">
          {data.timeline.map((t) => (
            <li key={t.encounter_id} data-testid={`ph-entry-${t.encounter_id}`} className="relative rounded-2xl border border-pink-100 bg-white p-4">
              <span className="absolute -left-[27px] top-5 h-3 w-3 rounded-full bg-[#DB2777] ring-4 ring-pink-100" />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-heading font-bold text-slate-800">{t.service || "-"}</p>
                  <p className="text-xs text-slate-500">{t.tanggal || "-"}{t.jam ? ` · ${t.jam}` : ""} · {t.practitioner_nama || "-"}{t.lokasi ? ` · ${t.lokasi}` : ""}</p>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${TONE[t.status] || "bg-slate-100"}`}>{t.status}</span>
              </div>
              {data.can_view_clinical && ((t.diagnoses && t.diagnoses.length > 0) || t.tindakan || (t.vital && Object.values(t.vital).some((v) => v !== "" && v != null))) && (
                <div className="mt-2 space-y-1 rounded-lg bg-[#FAF7FF] px-3 py-2 text-xs text-slate-600" data-testid={`ph-clinical-${t.encounter_id}`}>
                  {t.vital && Object.values(t.vital).some((v) => v !== "" && v != null) && (
                    <p><span className="font-bold text-[#6A38C7]">Vital:</span> {[t.vital.sistolik && t.vital.diastolik ? `TD ${t.vital.sistolik}/${t.vital.diastolik}` : null, t.vital.nadi ? `Nadi ${t.vital.nadi}` : null, t.vital.suhu ? `Suhu ${t.vital.suhu}°C` : null, t.vital.spo2 ? `SpO₂ ${t.vital.spo2}%` : null, t.vital.berat ? `BB ${t.vital.berat}kg` : null].filter(Boolean).join(" · ")}</p>
                  )}
                  {t.diagnoses && t.diagnoses.length > 0 && <p><span className="font-bold text-[#6A38C7]">Diagnosis:</span> {t.diagnoses.map((d) => `${d.code} ${d.name}`).join("; ")}</p>}
                  {t.tindakan && <p><span className="font-bold text-[#6A38C7]">Tindakan:</span> {t.tindakan}</p>}
                </div>
              )}
              <button data-testid={`ph-open-${t.encounter_id}`} onClick={() => nav(`/rekam-medis/${t.encounter_id}`)} className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-[#7C4DE0] px-3 py-1.5 text-xs font-bold text-white active:scale-95"><FolderOpen size={14} weight="fill" /> Buka Rekam Medis</button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
